import {gql} from '@apollo/client';
import {useCallback, useRef} from 'react';

import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';

import {
  AgentHomepageRoutineExecutionsQuery,
  AgentHomepageRoutineExecutionsQueryVariables,
  AgentHomepageRoutineExecutionsQuery_routineExecutions_RoutineExecutionListingNode_items_RoutineExecutionNode as AgentHomepageRoutineExecutionItem,
} from './__generated/AgentHomepageRoutineExecutionsQuery';

export const AGENT_HOMEPAGE_ROUTINE_EXECUTIONS_QUERY = gql`
  query AgentHomepageRoutineExecutionsQuery(
    $routineId: StrictString!
    $first: StrictInt
    $after: StrictString
  ) @engComponent(name: LLM) @skipAudit {
    routineExecutions(routineId: $routineId, first: $first, after: $after) {
      nextToken
      total
      items {
        id
        status
        executionKind
        chatSession {
          id
        }
        createdAt
      }
    }
  }
`;

export type AgentHomepageRoutineExecution = AgentHomepageRoutineExecutionItem;

const DEFAULT_PAGE_SIZE = 15;

const EMPTY_RUNS: ReadonlyArray<AgentHomepageRoutineExecutionItem> = [];

interface UseAgentHomepageRoutineExecutionsResult {
  runs: ReadonlyArray<AgentHomepageRoutineExecutionItem>;
  total: number;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Fetches an Agent homepage's execution history, including the execution kind used for activity
 * labels and icons.
 */
export default function useAgentHomepageRoutineExecutions({
  routineId,
  enabled = true,
}: {
  routineId: string | null;
  enabled?: boolean;
}): UseAgentHomepageRoutineExecutionsResult {
  const isFetchingMoreRef = useRef(false);

  const queryResult = useSkippableBenchlingQuery<
    AgentHomepageRoutineExecutionsQuery,
    AgentHomepageRoutineExecutionsQueryVariables
  >(AGENT_HOMEPAGE_ROUTINE_EXECUTIONS_QUERY, {
    skip: !enabled || routineId == null,
    variables: {
      routineId: routineId ?? '',
      first: DEFAULT_PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  const data = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;
  const runs = data?.routineExecutions.items ?? EMPTY_RUNS;
  const total = data?.routineExecutions.total ?? 0;
  const nextToken = data?.routineExecutions.nextToken ?? '';
  const hasMore = nextToken !== '';

  const isLoading = enabled && routineId != null ? queryResult.type === QueryResultType.PENDING : false;
  const isError = queryResult.type === QueryResultType.ERROR;

  const {fetchMore} = queryResult;

  const loadMore = useCallback(() => {
    if (routineId == null || nextToken === '' || isFetchingMoreRef.current) {
      return;
    }
    isFetchingMoreRef.current = true;
    fetchMore({
      variables: {
        routineId,
        first: DEFAULT_PAGE_SIZE,
        after: nextToken,
      },
      updateQuery: (previous, {fetchMoreResult}) => {
        if (fetchMoreResult == null) {
          return previous;
        }
        return {
          ...fetchMoreResult,
          routineExecutions: {
            ...fetchMoreResult.routineExecutions,
            total: previous.routineExecutions.total,
            items: [...previous.routineExecutions.items, ...fetchMoreResult.routineExecutions.items],
          },
        };
      },
    })
      .catch(() => {
        // A failed page surfaces through the query result's error state on the next render.
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [fetchMore, nextToken, routineId]);

  return {
    runs,
    total,
    isLoading,
    isError,
    hasMore,
    loadMore,
  };
}
